/* eslint-disable */

import { useState, useEffect } from "react";
import { createContainer } from "unstated-next";
import {
  fetchSearch,
  buildTotalPages,
  refreshPage,
  stringifyUrl,
  parseQueryStr,
  topPath,
  basePath,
  isCompanyPortalContext,
  priceCategoryIdsSanitizer,
  paramsPairs,
  initialRequestState,
  createRequestParam,
  restoreSeoState,
  isPageForSeo,
  paramsToJson,
  replaceH1text,
  changeHeaderImgAndHref,
  appendNoindex,
  removeNoindex,
  appendCanonicalTag,
  removeCanonicalTag,
  setCookieSearchHistories,
  sortResults,
} from "../../utils/search";
import {
  useDidUpdateEffect,
  useSearchIds,
  useAreaKeywords,
  useRangeActivities,
} from "../../utils/customHooks";
import camelCase from "../../utils/camelCase";

const useArticleSearchContainer = (initialState = {}) => {
  /* =========================================================
  * requestState
  * Elasticsearch::Articleの検索パラメータに用いる状態
  ========================================================= */
  const [word, setWord] = useState(initialState.request.word || "");
  const [page, setPage] = useState(initialState.request.page || 1);
  const [priceId, setPriceId] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [geoPoints, setGeoPoints] = useState(
    initialState.request.geo_points || []
  );
  const [areaKeywords, setAreaKeywords] = useAreaKeywords(
    initialState.request,
    initialState.items,
    initialState.vol_or_job
  );
  const [searchIds, setSearchIds] = useSearchIds(initialState.request);
  const [rangeActivities, setRangeActivities] = useRangeActivities(
    initialState.request
  );
  // ElasticsearchのDB設計ではonlineはareaと同列扱いのためareaIdsに含まれるが，検索UIの挙動が違うのでこの変数が必要．
  const extendedAreaChecked = {
    onlineChecked:
      Array.isArray(searchIds.areaIds) && searchIds.areaIds.includes("online"), // たまにSSRでエラーが出るので`Array.isArray()`で対策
    canJoinFarawayChecked:
      Array.isArray(searchIds.areaIds) &&
      searchIds.areaIds?.includes("can_join_faraway"),
  };
  // 状態をまとめる
  const requestState = {
    word,
    page,
    priceId,
    startDate,
    endDate,
    geoPoints,
    areaKeywords,
    ...searchIds,
    ...rangeActivities,
  };
  // useStateの更新関数をまとめる
  const setRequestState = {
    setWord,
    setPage,
    setPriceId,
    setStartDate,
    setEndDate,
    setGeoPoints,
    setAreaKeywords,
    ...setSearchIds,
    ...setRangeActivities,
  };
  // コールバック処理で参照する状態を配列に
  const searchDeps = [
    word,
    page,
    startDate,
    endDate,
    geoPoints,
    areaKeywords,
    ...Object.values(rangeActivities),
  ].concat(
    Object.keys(paramsPairs).map((key) => searchIds[`${camelCase(key)}Ids`])
  );
  const searchDepsExceptForAreaIds = [
    startDate,
    endDate,
    ...Object.values(extendedAreaChecked),
    ...Object.values(rangeActivities),
  ].concat(
    Object.keys(paramsPairs)
      .filter((k) => k !== "area")
      .map((key) => searchIds[`${camelCase(key)}Ids`])
  );
  // requestParamの生成
  const requestParam = createRequestParam(requestState);

  /* =========================================================
  * responseState
  * Elasticsearch::Articleのレスポンスに関する状態
  ========================================================= */
  const sortedResults = sortResults(
    initialState.response.hits.hits.map((hit) => hit._source)
  );
  const [results, setResults] = useState(sortedResults);
  const [total, setTotal] = useState(initialState.response.hits.total.value);
  const [totalPages, setTotalPages] = useState(
    buildTotalPages(initialState.response.hits.total.value)
  );
  const [aggs, setAggs] = useState(initialState.response.aggregations);
  const [aggsTotal, setAggsTotal] = useState(
    initialState.response.hits.total.value
  );
  /* =========================================================
  * サブコンテンツに関する状態
  ========================================================= */
  const [recommendPartTime, setRecommendPartTime] = useState(
    initialState.recommend_part_time || []
  );
  const [recommendOnline, setRecommendOnline] = useState(
    initialState.recommend_online || []
  );

  // 使っていない更新関数があるが、定義方法は下記のようにしかできない（アンダースコアなどで定義できない）ため取り急ぎルールから除外されるようにしている
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const [prContents, setPrContents] = useState(initialState.pr_contents || []);
  const [ads, setAds] = useState(initialState.ads || {});
  const [relatedAreaPath, setRelatedAreaPath] = useState(
    initialState.related_area_links || {}
  );
  /* eslint-enable @typescript-eslint/no-unused-vars */
  // adSetting追加
  /* =========================================================
  * フォームに関する状態
  ========================================================= */
  const [isSearching, setIsSearching] = useState(false);
  const [isRestoringState, setIsRestoringState] = useState(false);
  const [lastPageUrlRefreshedAt, setLastPageUrlRefreshedAt] = useState(
    Date.now()
  );
  const [volOrJob, setVolOrJob] = useState(initialState.vol_or_job);
  const [requestFromSp] = useState(initialState.requestFromSp); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [volJobSwitchPathName, setVolJobSwitchPathName] = useState(
    initialState.vol_job_switch_path_name
  );
  const [detailSearch, setDetailSearch] = useState(false);
  /* =========================================================
  * 検索実行前後のURLやページタイトルを管理するための状態
  ========================================================= */
  const [initialLocationPath, setInitialLocationPath] = useState(
    initialState.location_path
  );
  const [baseTitle, setBaseTitle] = useState(initialState.title);
  /* =========================================================
  * その他、initialStateからprops受け取りなど
  ========================================================= */
  const wrapperHtmlId = initialState.wrapper_html_id;
  const wrapperHtmlData = initialState.wrapper_html_data;

  // ブラウザバック時に状態の復元 & 再検索
  // SEO用ページからSEO用ページへのブラウザバックは設計上起こり得ないため、想定せずに実装している
  useEffect(() => {
    window.onpopstate = async () => {
      setIsRestoringState(true);
      let restoredRequestState = parseQueryStr(
        location.search,
        initialRequestState,
        paramsPairs
      );
      bulkSetRequestStates(restoredRequestState);
      if (isPageForSeo(initialLocationPath)) {
        bulkSetRequestStates(initialState.request);
        const restoredSeoState = restoreSeoState(initialState.request);
        restoredRequestState = { ...restoredRequestState, ...restoredSeoState };
      }
      searchAfterBrouserBack(restoredRequestState);
      setIsRestoringState(false);
      setLastPageUrlRefreshedAt(Date.now());
    };
  }, []);

  useEffect(() => {
    handleChangeRequestStateForMailNotification();
    setCookieSearchHistories({ vol_or_job: volOrJob, ...requestParam });
    const customEvents = [
      new CustomEvent("react_rendering"),
      new CustomEvent("gtm_article_algorithm_rendering", {
        detail: { wrapperSelector: `#${wrapperHtmlId}` },
      }),
      new CustomEvent("gtm_ecommerce_item_list_rendering", {
        detail: {
          wrapperSelector: `#${wrapperHtmlId}`,
          userType: "person",
          globalIndex: wrapperHtmlId === "gtmMainSearch", // gtmMainSearchの場合のみtrue
        },
      }),
    ];
    customEvents.forEach((event) => window.dispatchEvent(event));
  }, [results]);

  useDidUpdateEffect(() => {
    if (isRestoringState === true) return;
    aggsSearch();
  }, [searchIds.areaIds, geoPoints, areaKeywords]);

  useDidUpdateEffect(() => {
    if (isRestoringState === true) return;
    requestFromSp || detailSearch ? aggsSearch() : search();
    if (requestFromSp) setVolJobSwitchPathName(null); // volJobSwitcherのリンクをなくす
  }, searchDepsExceptForAreaIds);

  // TODO: abテスト後コメント削除
  // abテスト用 class_b
  // sp検索フォーム下の「条件を追加」の項目の選択時にsearch()を実行
  useDidUpdateEffect(() => {
    const isInducingFormClicked = $(".jsInducingForm").prop("clicked");
    $(".jsInducingForm").prop("clicked", false);
    if (
      requestFromSp &&
      (searchIds.positionIds.length > 0 ||
        searchIds.workingFormIds.length > 0) &&
      isInducingFormClicked
    ) {
      search();
      setVolJobSwitchPathName(null); // volJobSwitcherのリンクをなくす
    }
  }, [searchIds.positionIds, searchIds.workingFormIds]);

  // priceIdが変化したタイミングでpriceCategoryIdsをセット
  useDidUpdateEffect(() => {
    const targetIds = priceCategoryIdsSanitizer(priceId);
    setSearchIds.setPriceCategoryIds(targetIds);
  }, [priceId]);

  // public

  const aggsSearch = async () => {
    const url = stringifyUrl(basePath(volOrJob), {
      ...requestParam,
      ...{ aggs: true },
    });
    const res = await fetchSearch(url);
    setAggsTotal(res.total);
    setAggs(res.aggs);
  };

  const search = async (async = true) => {
    volOrJob == "job" ? await jobSearch(async) : await volSearch(async);
    // adManagerRefresh();
    const url = stringifyUrl(basePath(volOrJob), { ...requestParam });
    if (!url.includes("/companies/")) {
      hideClosingArticle();
    }
  };

  const otherTypeSearch = async () => {
    const otherType = ["volunteer", "job"].find((k) => k !== volOrJob);
    otherType == "job" ? await jobSearch() : await volSearch();
    setVolOrJob(otherType);
    changeHeaderImgAndHref(otherType);
  };

  const switchPageSearch = async (newPage) => {
    // adManagerRefresh();
    setPage(newPage);
    const query = { ...requestParam, page: newPage };
    const url = stringifyUrl(basePath(volOrJob), query);
    const res = await fetchSearch(url);
    // NOTE: canonical URL自己参照のため、1ページ目の場合はpageパラメータを除去
    const urlForHistory = isPageForSeo(location.pathname)
      ? stringifyUrl(location.pathname, {
          ...(newPage !== 1 && { page: newPage }), // 1ページ目以外でのみpageパラメータを含める
        })
      : (() => {
          const queryWithoutPage = { ...query };
          delete queryWithoutPage.page;
          if (newPage !== 1) {
            queryWithoutPage.page = newPage; // 1ページ目以外でのみpageパラメータを含める
          }
          return stringifyUrl(topPath(volOrJob), queryWithoutPage);
        })();
    const title = newPage == 1 ? baseTitle : `${baseTitle} ${newPage}ページ目`;
    const scrollY = requestFromSp ? 0 : 145;
    refreshPage(urlForHistory, title, scrollY);
    setResponseState(res);
    setLastPageUrlRefreshedAt(Date.now());
    replaceH1text(requestFromSp, res.hits.total.value, title);
    if (isPageForSeo(location.pathname)) {
      appendCanonicalTag(location.href);
    }
  };

  // private

  const jobSearch = async (async = true) => {
    const query = { ...requestParam, page: 1 };
    const path = async ? basePath("job") : topPath("job");
    const url = stringifyUrl(path, query);
    if (async) {
      const res = await handleAsyncAction(fetchSearch(url));
      setRefreshPageState(topPath("job"), res.hits.total.value);
      setResponseState(res);
    } else {
      window.location.href = url;
    }
  };

  const volSearch = async (async = true) => {
    const query = { ...requestParam, page: 1 };
    const path = async ? basePath("volunteer") : topPath("volunteer");
    const mainUrl = stringifyUrl(path, query);
    if (async) {
      // NOTE: 企業ポータル(subdomain / LVJ旧URL)では `/sub_search` を呼ばない。
      //   - 役割: activo top-level 用の cross-promo (recommend_part_time / recommend_online) 補助APIで、
      //     企業ポータルでは初期描画時に企業スコープでセット済みのため refetch 不要。
      //   - 加えて subdomain ルーティング上は未公開で 404 となり、Promise.all reject により
      //     setResponseState 等まで到達せず検索結果が更新されない問題があった。
      const fetchSubPromise = isCompanyPortalContext()
        ? Promise.resolve(null)
        : fetchSearch(stringifyUrl(Routes.sub_search_index_path(), query));
      const [res, resSub] = await Promise.all([
        handleAsyncAction(fetchSearch(mainUrl)),
        fetchSubPromise,
      ]);
      setRefreshPageState(topPath("volunteer"), res.hits.total.value);
      setResponseState(res);
      if (resSub) setSubResponseState(resSub);
    } else {
      window.location.href = mainUrl;
    }
  };

  const searchAfterBrouserBack = async (restoredRequestState) => {
    const query = createRequestParam(restoredRequestState);
    let searchType = volOrJob;
    if (requestFromSp) {
      searchType = location.pathname.includes("job") ? "job" : "volunteer";
      setVolOrJob(searchType);
    }
    const url = stringifyUrl(basePath(searchType), query);
    const res = await handleAsyncAction(fetchSearch(url));
    setResponseState(res);
    if (isPageForSeo(location.pathname)) {
      removeNoindex();
      appendCanonicalTag(location.href);
    } else {
      appendNoindex();
    }
  };

  const handleChangeRequestStateForMailNotification = () => {
    const newParamsJson = paramsToJson({
      ...requestParam,
      vol_or_job: volOrJob,
    });
    $("#jsMailNotification").attr("data-search-condition", newParamsJson); // NOTE: erbで記述された部分に値をセットするため、直接DOMを操作する形になっている
  };

  const setRefreshPageState = (baseHistoryPath, totalNum) => {
    const query = { ...requestParam, page: null };
    const url = stringifyUrl(baseHistoryPath, query);
    const title = "検索結果";
    setPage(1);
    setBaseTitle(title);
    setInitialLocationPath("");
    const scrollY = requestFromSp ? 0 : document.documentElement.scrollTop;
    refreshPage(url, title, scrollY);
    setLastPageUrlRefreshedAt(Date.now());
    replaceH1text(requestFromSp, totalNum, title);
    appendNoindex();
    removeCanonicalTag();
  };

  const setResponseState = (res) => {
    const sortedResponseResults = sortResults(
      res.hits.hits.map((hit) => hit._source)
    );
    setResults(sortedResponseResults);
    setTotal(res.hits.total.value);
    setAggsTotal(res.hits.total.value);
    setTotalPages(buildTotalPages(res.hits.total.value));
    // setAggs(res.aggregations);
  };

  const setSubResponseState = (res) => {
    setRecommendPartTime(res.recommend_part_time || []);
    setRecommendOnline(res.recommend_online || []);
  };

  const bulkSetRequestStates = (restoredRequestState) => {
    Object.keys(restoredRequestState).forEach((key) => {
      try {
        const functionName = `set${camelCase(key, { pascalCase: true })}`; // NOTE: 例："setWord"など
        if (typeof setRequestState[functionName] === "function") {
          // NOTE: functionNameがsetRequestStateで定義している場合に、restoredRequestStateのkeyを渡す
          setRequestState[functionName](restoredRequestState[key]);
        }
      } catch (error) {}
    });
  };

  /* =========================================================
  * ローダーの表示に関する処理をまとめる
  * @param asyncAction 扱いたいpromise
  ========================================================= */
  // ESlint：finallyの箇所にreturnが付いていない警告があるが、finallyにreturnを付けると別の警告（no-unsafe-finally）が出るので取り急ぎ除外
  const handleAsyncAction = async (asyncAction) => {
    // eslint-disable-line consistent-return
    let res;
    try {
      setIsSearching(true);
      res = await asyncAction;
      return res;
    } catch (e) {
    } finally {
      if (res.hits.total.value) {
        $("#jsResultShowerNum").text(res.hits.total.value);
      }
      setIsSearching(false);
      $("#jsResultShower").show().delay(2000).fadeOut();
    }
  };

  return {
    // state
    ...requestState,
    ...extendedAreaChecked,
    results,
    total,
    totalPages,
    aggs,
    aggsTotal,
    recommendPartTime,
    recommendOnline,
    prContents,
    ads,
    relatedAreaPath,
    isSearching,
    volOrJob,
    baseTitle,
    initialLocationPath,
    lastPageUrlRefreshedAt,
    requestFromSp,
    volJobSwitchPathName,
    // event handler
    ...setRequestState,
    setResults,
    search,
    otherTypeSearch,
    switchPageSearch,
    setIsRestoringState,
    setVolJobSwitchPathName,
    setDetailSearch,
    // dependency list
    searchDeps,
    // stateを計算した変数
    requestParam,
    // その他
    wrapperHtmlId,
    wrapperHtmlData,
  };
};

const ArticleSearchContainer = createContainer(useArticleSearchContainer);

export default ArticleSearchContainer;
